import React, { FC, MutableRefObject, ReactNode } from 'react';
import DOMPurify from 'dompurify';
import html2canvas from 'html2canvas';
import JsPdf from 'jspdf';
import { renderToStaticMarkup } from 'react-dom/server';
import { toast } from 'react-toastify';

import { PDF_HIDDEN_CLASS } from 'src/shared/constants';

/**
 * Allows exporting a snapshot of any html elements to a PDF file
 *
 * To hide an element from the PDF, add the className `PDF_HIDDEN_CLASS` to them
 */

type RenderCallback = (fct: () => Promise<void>) => JSX.Element;

interface Props {
  targetRef: MutableRefObject<HTMLDivElement | null>;
  filename: string;
  options: Record<string, any>;
  children: RenderCallback;
  scale?: number;
  header?: ReactNode;
  onLoad?: (loading: boolean) => void;
}

export const ComponentWithPdfExport: FC<Props> = ({
  targetRef,
  filename,
  options,
  onLoad,
  scale,
  header,
  children,
}) => {
  const exportToPdf = async () => {
    const target = targetRef.current;

    if (!target) {
      return;
    }

    const htmlElement: HTMLElement = document.createElement('div');

    try {
      if (onLoad) {
        onLoad(true);
      }

      htmlElement.className = 'pdf-export-container position-fixed p-5 w-100';
      htmlElement.style.zIndex = '-999999';
      htmlElement.innerHTML = DOMPurify.sanitize(renderToStaticMarkup(<>{header}</>));
      htmlElement.append(target.cloneNode(true));

      // remove all elements that needs to be removed in PDF
      const hiddenElements = htmlElement.getElementsByClassName(PDF_HIDDEN_CLASS);
      Array.from(hiddenElements).forEach((element) => element.remove());

      document.body.appendChild(htmlElement);

      const canvas = await html2canvas(htmlElement, {
        logging: false,
        scale,
      });

      const imgData = canvas.toDataURL();
      const pdf = new JsPdf(options);

      // @ts-ignore
      pdf.addImage(imgData, 'JPEG', 0, 0);
      pdf.save(filename);
    } catch (e: any) {
      toast.info(e, { type: toast.TYPE.ERROR });
      console.error(e);
    } finally {
      if (onLoad) {
        onLoad(false);
      }

      htmlElement.remove();
    }
  };

  return <>{children(exportToPdf)}</>;
};
